import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormikErrors, useFormik } from 'formik';
import React, { useRef, useState } from 'react'
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CustomerDetailsAPI from '../../API/customerDetailsAPI';
import Helper from '../../utils/Helper';
import { selectToken, setEmail } from '../user/userSlice';
import { loadCustomerDetails, selectCustomerDetails, setCustomerDetails } from './customerDetailSlice';
import { selectCustomer } from './customerSlice';
import { selectEditAccountOptions } from './editaccountOptionsSlice';
import config from '../../config.json';
import { selectIsMobileRoute } from '../mobile/mobileSlice';
import GooglePlace from '../../components/GooglePlace';
import MyAccountPage from '../../pages/MyAccountPage';
import Window from '../../utils/Window';

interface FormValues {
    email: string;
    f_name: string;
    l_name: string;
    address_one: string;
    address_two: string;
    gender: string;
    mobile_number: string;
    zip_code: string;
    user_city: string;
    user_state: string;
    birthdate: string;
}

interface Props {
    handleFirstAccordian: () => void;
    setErrorMsg: (errorMsg: string) => void;
}

function BasicInformationForm({ handleFirstAccordian, setErrorMsg }: Props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const token = useSelector(selectToken);
    const custData = useSelector(selectCustomer);
    const customerData = useSelector(selectCustomerDetails);
    const userSettings = useSelector(selectEditAccountOptions);
    const customerDetails = customerData?.user_data;
    const [busy, setBusy] = useState(false);
    const AppURL = useSelector(selectIsMobileRoute);
    const urlParams = AppURL ? `/?${AppURL}` : '';

    const validate = (values: FormValues) => {
        const errors: FormikErrors<FormValues> = {};
        if (values.mobile_number) {
            if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(values.mobile_number)) {
                errors.mobile_number = "Invalid mobile number";
            }
        }
        if (values.zip_code) {
            if (!/(^\d{5}$)|(^\d{5}-\d{4}$)/.test(values.zip_code)) {
                errors.zip_code = "Invalid zip code";
            }
        }
        return errors;
    }

    const formik = useFormik({
        initialValues: {
            email: custData?.email,
            f_name: customerDetails?.f_name,
            l_name: customerDetails?.l_name,
            address_one: customerDetails?.address_one,
            address_two: customerDetails?.address_two,
            gender: customerDetails?.gender,
            mobile_number: Helper.formatPhoneNo(customerDetails?.mobile_number),
            zip_code: customerDetails?.zip_code,
            user_city: customerDetails?.user_city,
            user_state: customerDetails?.user_state,
            birthdate: customerDetails?.birthdate,
        },
        validate,
        onSubmit: values => {
            setErrorMsg('');
            setBusy(true);
            CustomerDetailsAPI.updateBasicInformationCustomerDetails({
                email: custData.email,
                f_name: values?.f_name,
                l_name: values?.l_name,
                address_one: values?.address_one,
                address_two: values?.address_two,
                gender: values?.gender,
                mobile_number: Helper.formatPhoneNo(values?.mobile_number),
                zip_code: values?.zip_code,
                user_city: values?.user_city,
                user_state: values?.user_state,
                birthdate: values?.birthdate
            }).then((response) => {
                if ('code' in response) {
                    if ('message' in response) {
                        setErrorMsg(response.message);
                    } else {
                        setErrorMsg("An error has occurred. Please try again later.");
                    }
                } else if ('id' in response || 'email' in response) {
                    dispatch(setCustomerDetails(response));
                    dispatch(setEmail(response.email));
                }
            }).catch((e) => {
                setErrorMsg(e.message);
            }).finally(() => {
                dispatch(loadCustomerDetails(custData.email));
                setBusy(false);
                handleFirstAccordian();
            })
        }
    });

    const streetRef = useRef<any>();
    const townRef = useRef<any>();

    const handleCancel = () => {
        if (Window.isMobile()) {
            navigate(`/my-account/edit-account${urlParams}`);
        } else {
            navigate(`/my-account${urlParams}`);
        }
    }

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    if (!token) {
        return (
            <MyAccountPage />
        )
    }

    return (
        <Form>
            <Row>
                <Col className='mb-4' md={6}>
                    <Form.Group className="form-group">
                        <Form.Label>First name</Form.Label>
                        <Form.Control
                            id="f_name"
                            type="text"
                            value={formik?.values?.f_name}
                            onChange={formik.handleChange}
                            className="form-textbox"
                            placeholder='Enter your first name'
                        />
                    </Form.Group>
                </Col>
                <Col className='mb-4' md={6}>
                    <Form.Group className="form-group">
                        <Form.Label>Last name</Form.Label>
                        <Form.Control
                            id="l_name"
                            type="text"
                            value={formik?.values?.l_name}
                            onChange={formik.handleChange}
                            className="form-textbox"
                            placeholder='Enter your last name'
                        />
                    </Form.Group>
                </Col>
                <Col className='mb-4' md={6}>
                    <Form.Group className="form-group">
                        <Form.Label>Address 1</Form.Label>
                        <GooglePlace formik={formik} streetRef={streetRef} townRef={townRef} name={'address_one'}
                            city={'user_city'} state={'user_state'} postcode={'zip_code'}
                        />
                    </Form.Group>
                </Col>
                <Col className='mb-4' md={6}>
                    <Form.Group className="form-group">
                        <Form.Label>Address 2</Form.Label>
                        <Form.Control
                            id="address_two"
                            type="text"
                            value={formik?.values?.address_two}
                            onChange={formik.handleChange}
                            className="form-textbox"
                            placeholder="Apartment, suite, unit etc. (optional)"
                        />
                    </Form.Group>
                </Col>
                <Col className='mb-4' md={6}>
                    <Form.Group className="form-group">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                            id="user_city"
                            ref={townRef}
                            type="text"
                            name="user_city"
                            placeholder="Enter Town/Area"
                            value={formik.values.user_city}
                            onChange={formik.handleChange}
                            onKeyDown={handleKeyPress}
                        />
                    </Form.Group>
                </Col>
                <Col className='mb-4' md={6}>
                    <Form.Group className="mb-3 form-group required">
                        <Form.Label>State</Form.Label>
                        <div className='d-flex'>
                            <Form.Select className="form-textbox" name="user_state" value={formik?.values?.user_state} onChange={formik.handleChange}>
                                <option value="">Select State</option>
                                {config?.STATE_CODES?.map((entry) => (
                                    <option key={entry?.code} value={entry?.code}>{entry?.state}</option>
                                ))}
                            </Form.Select>
                        </div>
                    </Form.Group>
                </Col>
                <Col className='mb-4' md={6} lg={6}>
                    <Form.Group className="form-group">
                        <Form.Label>Zip Code</Form.Label>
                        <Form.Control
                            id="zip_code"
                            type="text"
                            maxLength={10}
                            placeholder="Enter Postcode / ZIP"
                            value={formik.values.zip_code}
                            onChange={formik.handleChange}
                        />
                        {formik?.errors?.zip_code &&
                            <Form.Control.Feedback type="invalid">
                                {formik?.errors?.zip_code}
                            </Form.Control.Feedback>}
                    </Form.Group>
                </Col>
                <Col className='mb-4' md={6}>
                    <Form.Group className="form-group">
                        <Form.Label>Gender</Form.Label>
                        <div className='d-flex'>
                            <Form.Select className="form-textbox" name="gender" value={formik?.values?.gender} onChange={formik.handleChange}>
                                <option value="">Select Gender</option>
                                {userSettings?.gender_option?.map((items: any) => (
                                    <option key={items} value={items}>{items}</option>
                                ))}
                            </Form.Select>
                        </div>
                    </Form.Group>
                </Col>
                <Col className='mb-4' md={6}>
                    <Form.Group className="form-group">
                        <Form.Label>Mobile Phone</Form.Label>
                        <Form.Control
                            id="mobile_number"
                            type="text"
                            maxLength={14}
                            isValid={Boolean(formik?.values?.mobile_number) &&
                                !Boolean(formik?.errors?.mobile_number)}
                            isInvalid={Boolean(formik?.errors?.mobile_number)}
                            value={formik?.values?.mobile_number}
                            onChange={(e) => formik.setFieldValue("mobile_number", Helper.formatPhoneNo(e.target.value))}
                            placeholder='Enter your mobile number'
                        />
                        {formik?.errors?.mobile_number &&
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.mobile_number}
                            </Form.Control.Feedback>}
                    </Form.Group>
                </Col>
                <Col className='mb-4' md={6} lg={6}>
                    <Form.Group className="form-group">
                        <Form.Label>Birthdate</Form.Label>
                        <Form.Control
                            id="birthdate"
                            type="date"
                            className="form-textbox text-uppercase"
                            value={formik?.values?.birthdate}
                            onChange={formik.handleChange}
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <div className={`d-flex flex-wrap ${Window.isMobile() ? 'justify-content-center' : 'justify-content-end'} mt-2`}>
                <Button
                    className={`${Window.isMobile() ? 'col-5' : 'col-12 col-md-auto'} mx-2 me-md-2 mb-2`}
                    variant="outline-dark"
                    disabled={busy}
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
                {busy ?
                    <Button
                        variant='success'
                        className={`${Window.isMobile() ? 'col-5' : 'col-12 col-md-auto'} mx-2 mb-2`}
                        disabled>
                        {Window.isMobile() ? 'Saving' : 'Update & Continue Editing'}...&nbsp;&nbsp;
                        <Spinner animation="border" as="span" size="sm" />
                    </Button> :
                    <Button
                        variant='success'
                        className={`${Window.isMobile() ? 'col-5' : 'col-12 col-md-auto'} mx-2 mb-2`}
                        type="submit" onClick={(e: any) => { e.preventDefault(); formik.handleSubmit(); }}>
                        {Window.isMobile() ? 'Save' : 'Update & Continue Editing'} &nbsp;&nbsp;
                        {!Window.isMobile() &&
                            <FontAwesomeIcon
                                role="button"
                                icon={faArrowUpRightFromSquare}
                                size={'1x' as SizeProp}
                            />}
                    </Button>
                }
            </div>
        </Form>
    )
}

export default BasicInformationForm